import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, adminFetch } from "./auth";
import { useSiteContent } from "../context/ContentContext";
import { WorkflowBar, uid } from "./editorControls";
import { HomepageEditor } from "./HomepageEditor";
import { EventsEditor, validateEvents } from "./EventsEditor";
import { DocumentsEditor, validateDocuments } from "./DocumentsEditor";
import {
  contact as defaultContact,
  winterHours as defaultHours,
  rates as defaultRates,
  golfEvents,
  banquets as defaultBanquets,
  aboutContent,
  memberships as defaultMemberships,
  juniorGolf as defaultJuniorGolf,
  juniorCamp as defaultJuniorCamp,
  sandTrap as defaultSandTrap,
  homepage as defaultHomepage,
  membershipDocuments as defaultDocs,
  siteImages,
} from "../data/redlandGolfContent";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = {
  about: siteImages.palmCourse,
  memberships: siteImages.clubhouse,
  juniorGolf: siteImages.junior,
  juniorCamp: siteImages.junior,
  sandTrap: siteImages.dining,
};

const SECTIONS = [
  { key: "homepage", label: "Homepage" },
  { key: "announcements", label: "Announcements" },
  { key: "events", label: "Events" },
  { key: "hours", label: "Business Hours" },
  { key: "contact", label: "Contact Info" },
  { key: "rates", label: "Rates & Specials" },
  { key: "banquetInfo", label: "Banquet Info" },
  { key: "gallery", label: "Gallery Photos" },
  { key: "about", label: "About Page" },
  { key: "memberships", label: "Memberships Page" },
  { key: "membershipDocuments", label: "Membership Documents" },
  { key: "juniorGolf", label: "Junior Golf Page" },
  { key: "juniorCamp", label: "Junior Camp Page" },
  { key: "sandTrap", label: "Sand Trap Page" },
];

function digits(s) {
  return (s || "").replace(/\D/g, "");
}

const mergeDocs = (over) =>
  Object.fromEntries(Object.keys(defaultDocs).map((k) => [k, { ...defaultDocs[k], ...((over || {})[k] || {}) }]));

function stableStringify(obj) {
  if (obj === null || obj === undefined) return "null";
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(",")}]`;
  if (typeof obj === "object") {
    return `{${Object.keys(obj).sort().filter((k) => k !== "id").map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
  }
  return JSON.stringify(obj);
}

export function AdminDashboard() {
  const { admin, logout } = useAuth();
  const { refresh } = useSiteContent();
  const navigate = useNavigate();
  const [active, setActive] = useState("homepage");
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const flashTimer = useRef(null);

  // "live" baseline per content key (what the public site currently shows)
  const [live, setLive] = useState({});

  // editable state
  const [homepage, setHomepage] = useState(defaultHomepage);
  const [announcements, setAnnouncements] = useState({ enabled: false, items: [] });
  const [events, setEvents] = useState(golfEvents.events);
  const [hours, setHours] = useState(defaultHours);
  const [contactInfo, setContactInfo] = useState(defaultContact);
  const [rates, setRates] = useState({ ...defaultRates, specials: "" });
  const [banq, setBanq] = useState(defaultBanquets);
  const [about, setAbout] = useState({ ...aboutContent, heroImage: HERO.about });
  const [mems, setMems] = useState({ ...defaultMemberships, heroImage: HERO.memberships });
  const [jg, setJg] = useState({ ...defaultJuniorGolf, heroImage: HERO.juniorGolf });
  const [jc, setJc] = useState({ ...defaultJuniorCamp, heroImage: HERO.juniorCamp });
  const [docs, setDocs] = useState(defaultDocs);
  const [st, setSt] = useState({ ...defaultSandTrap, heroImage: HERO.sandTrap });

  useEffect(() => {
    (async () => {
      try {
        await loadState();
      } catch (e) {
        setStatus({ type: "error", text: "Could not load current content. Please refresh." });
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadState = async () => {
    const state = await adminFetch("/api/admin/state");
    const pub = state.published || {};
    const dr = state.draft || {};
    const pick = (key, def) => (dr[key] !== undefined ? dr[key] : pub[key] !== undefined ? pub[key] : def);
    const withHero = (def, key, over) => ({ ...def, heroImage: HERO[key], ...(over || {}) });

    setHomepage(pick("homepage", defaultHomepage));
    setAnnouncements(pick("announcements", { enabled: false, items: [] }));
    const evs = pick("events", golfEvents.events);
    setEvents((Array.isArray(evs) ? evs : golfEvents.events).map((e, idx) => ({ id: e.id || uid(), order: e.order ?? idx, visible: e.visible !== false, featured: !!e.featured, ...e })));
    setHours({ ...defaultHours, ...(pick("hours", null) || {}) });
    setContactInfo({ ...defaultContact, ...(pick("contact", null) || {}) });
    setRates({ specials: "", ...defaultRates, ...(pick("rates", null) || {}) });
    const b = { ...defaultBanquets, ...(pick("banquets", null) || {}) };
    setBanq({
      ...b,
      gallery: (b.gallery || []).map((g) => ({ id: g.id || uid(), ...g })),
      portraitGallery: (b.portraitGallery || []).map((g) => ({ id: g.id || uid(), ...g })),
    });
    setAbout(withHero(aboutContent, "about", pick("about", null)));
    setMems(withHero(defaultMemberships, "memberships", pick("memberships", null)));
    setDocs(mergeDocs(pick("membershipDocuments", null)));
    const jgv = withHero(defaultJuniorGolf, "juniorGolf", pick("juniorGolf", null));
    setJg({ ...jgv, gallery: (jgv.gallery || []).map((g) => ({ id: g.id || uid(), ...g })) });
    setJc(withHero(defaultJuniorCamp, "juniorCamp", pick("juniorCamp", null)));
    const stv = withHero(defaultSandTrap, "sandTrap", pick("sandTrap", null));
    setSt({ ...stv, menuGallery: (stv.menuGallery || []).map((g) => ({ id: g.id || uid(), ...g })) });

    // live baseline = published override or default
    const livePick = (key, def) => (pub[key] !== undefined ? pub[key] : def);
    setLive({
      homepage: livePick("homepage", defaultHomepage),
      announcements: livePick("announcements", { enabled: false, items: [] }),
      events: livePick("events", golfEvents.events),
      hours: { ...defaultHours, ...(livePick("hours", null) || {}) },
      contact: normalizeContact({ ...defaultContact, ...(livePick("contact", null) || {}) }),
      rates: { specials: "", ...defaultRates, ...(livePick("rates", null) || {}) },
      banquets: livePick("banquets", defaultBanquets),
      about: withHero(aboutContent, "about", livePick("about", null)),
      memberships: withHero(defaultMemberships, "memberships", livePick("memberships", null)),
      membershipDocuments: mergeDocs(livePick("membershipDocuments", null)),
      juniorGolf: withHero(defaultJuniorGolf, "juniorGolf", livePick("juniorGolf", null)),
      juniorCamp: withHero(defaultJuniorCamp, "juniorCamp", livePick("juniorCamp", null)),
      sandTrap: withHero(defaultSandTrap, "sandTrap", livePick("sandTrap", null)),
    });
  };

  const flash = (type, text) => {
    if (flashTimer.current) {
      clearTimeout(flashTimer.current);
      flashTimer.current = null;
    }
    setStatus({ type, text });
    if (type === "success") {
      flashTimer.current = setTimeout(() => setStatus(null), 4500);
    }
  };

  // section -> {contentKey, value, previewPath}
  const metaFor = (sectionKey) => {
    const map = {
      homepage: { key: "homepage", value: homepage, preview: "/" },
      announcements: { key: "announcements", value: announcements, preview: "/" },
      events: { key: "events", value: events, preview: "/" },
      hours: { key: "hours", value: hours, preview: "/contact-us" },
      contact: { key: "contact", value: contactInfo, preview: "/contact-us" },
      rates: { key: "rates", value: rates, preview: "/rates" },
      banquetInfo: { key: "banquets", value: banq, preview: "/banquets" },
      gallery: { key: "banquets", value: banq, preview: "/banquets" },
      about: { key: "about", value: about, preview: "/about-us" },
      memberships: { key: "memberships", value: mems, preview: "/memberships" },
      membershipDocuments: { key: "membershipDocuments", value: docs, preview: "/memberships", hash: "#membership-documents" },
      juniorGolf: { key: "juniorGolf", value: jg, preview: "/junior-golf" },
      juniorCamp: { key: "juniorCamp", value: jc, preview: "/junior-camp" },
      sandTrap: { key: "sandTrap", value: st, preview: "/the-sand-trap" },
    };
    return map[sectionKey];
  };

  const buildPayload = (key, value) => {
    if (key === "contact") return normalizeContact(value);
    if (key === "events") return value.map((e, idx) => ({ ...e, order: e.order ?? idx }));
    return value;
  };

  const statusOf = (key, value) => {
    const cur = buildPayload(key, value);
    const baseline = live[key];
    return stableStringify(cur) === stableStringify(baseline) ? "published" : "draft";
  };

  const saveDraft = async (silent = false) => {
    const m = metaFor(active);
    const payload = buildPayload(m.key, m.value);
    const errs = validationErrors(m.key, payload);
    if (errs.length) { flash("error", errs[0]); return false; }
    setBusy(true);
    try {
      await adminFetch("/api/admin/content", { method: "PUT", json: { section: m.key, data: payload } });
      if (m.key === "contact") setContactInfo(payload);
      if (!silent) flash("success", "Draft saved. Use Preview to review, then Publish to go live.");
      return true;
    } catch (e) {
      flash("error", e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async () => {
    const m = metaFor(active);
    const ok = await saveDraft(true);
    if (!ok) return;
    flash("success", "Draft saved — opening preview in a new tab.");
    window.open(`${m.preview}?preview=1${m.hash || ""}`, "_blank", "noopener");
  };

  const onPublish = async () => {
    const m = metaFor(active);
    const payload = buildPayload(m.key, m.value);
    const errs = validationErrors(m.key, payload);
    if (errs.length) { flash("error", errs[0]); return; }
    const label = SECTIONS.find((s) => s.key === active)?.label || "changes";
    if (!window.confirm(`Publish ${label}? This goes live on the website immediately.`)) return;
    setBusy(true);
    try {
      await adminFetch("/api/admin/content", { method: "PUT", json: { section: m.key, data: payload } });
      await adminFetch("/api/admin/publish", { method: "POST", json: { section: m.key } });
      setLive((prev) => ({ ...prev, [m.key]: payload }));
      if (m.key === "contact") setContactInfo(payload);
      await refresh();
      flash("success", `${label} published successfully — it is now live.`);
    } catch (e) {
      flash("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin");
  };

  const onDiscard = async () => {
    const m = metaFor(active);
    if (statusOf(m.key, m.value) !== "draft") return;
    if (!window.confirm("Discard your unpublished draft changes for this section and revert to the live version?")) return;
    setBusy(true);
    try {
      await adminFetch("/api/admin/discard", { method: "POST", json: { section: m.key } });
      await loadState();
      flash("success", "Draft discarded — reverted to the published version.");
    } catch (e) {
      flash("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="admin-loading">Loading dashboard…</div>;

  const m = metaFor(active);
  const workflow = {
    status: statusOf(m.key, m.value),
    onSaveDraft: () => saveDraft(false),
    onPreview,
    onPublish,
    onDiscard,
    busy,
  };

  return (
    <div className="admin-dash" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <div className="admin-topbar-brand">Redland Golf — Content Manager</div>
        <div className="admin-topbar-actions">
          <span className="admin-who" data-testid="admin-current-user">Signed in as {admin?.username}</span>
          <a className="admin-link" href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site">View Site</a>
          <Link className="admin-link" to="/admin/change-password" data-testid="admin-change-password-link">Change Password</Link>
          <button className="button button-secondary admin-logout" onClick={doLogout} data-testid="admin-logout-button">Log Out</button>
        </div>
      </header>

      <div className="admin-body">
        <nav className="admin-nav" data-testid="admin-section-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              className={active === s.key ? "is-active" : ""}
              onClick={() => setActive(s.key)}
              data-testid={`admin-tab-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <main className="admin-content">
          {status ? (
            <div className={`admin-alert admin-alert-${status.type}`} data-testid="admin-status">{status.text}</div>
          ) : null}

          {active === "homepage" && <HomepageEditor value={homepage} onChange={setHomepage} onStatus={flash} />}
          {active === "announcements" && <AnnouncementsEditor value={announcements} onChange={setAnnouncements} />}
          {active === "events" && <EventsEditor value={events} onChange={setEvents} onStatus={flash} />}
          {active === "hours" && <HoursEditor value={hours} onChange={setHours} />}
          {active === "contact" && <ContactEditor value={contactInfo} onChange={setContactInfo} />}
          {active === "rates" && <RatesEditor value={rates} onChange={setRates} />}
          {active === "banquetInfo" && <BanquetInfoEditor value={banq} onChange={setBanq} />}
          {active === "gallery" && <GalleryEditor value={banq} onChange={setBanq} onStatus={flash} />}
          {active === "about" && <AboutEditor value={about} onChange={setAbout} onStatus={flash} />}
          {active === "memberships" && <MembershipsEditor value={mems} onChange={setMems} onStatus={flash} />}
          {active === "membershipDocuments" && <DocumentsEditor value={docs} onChange={setDocs} onStatus={flash} />}
          {active === "juniorGolf" && <JuniorGolfEditor value={jg} onChange={setJg} onStatus={flash} />}
          {active === "juniorCamp" && <JuniorCampEditor value={jc} onChange={setJc} onStatus={flash} />}
          {active === "sandTrap" && <SandTrapEditor value={st} onChange={setSt} onStatus={flash} />}

          <WorkflowBar {...workflow} />
        </main>
      </div>
    </div>
  );
}

function validationErrors(key, payload) {
  if (key === "events") return validateEvents(payload);
  if (key === "membershipDocuments") return validateDocuments(payload);
  return [];
}

function normalizeContact(c) {
  return {
    ...c,
    phoneHref: `tel:+1${digits(c.phoneDisplay)}`,
    emailHref: `mailto:${c.email}`,
    fullAddress: `${c.addressLine1}, ${c.cityStateZip}`,
    directionsHref: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.addressLine1}, ${c.cityStateZip}`)}`,
  };
}

function AnnouncementsEditor({ value, onChange }) {
  const items = value.items || [];
  const setItem = (i, text) => onChange({ ...value, items: items.map((it, idx) => (idx === i ? { ...it, text } : it)) });
  return (
    <section className="admin-section">
      <h2>Homepage Announcements</h2>
      <p className="admin-hint">Show an alert banner at the top of the homepage. Turn it off to hide it.</p>
      <label className="admin-check">
        <input type="checkbox" checked={!!value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} data-testid="announcements-enabled" />
        Show announcement banner
      </label>
      {items.map((it, i) => (
        <div className="admin-row" key={it.id || i}>
          <input value={it.text} onChange={(e) => setItem(i, e.target.value)} placeholder="Announcement text" data-testid={`announcement-input-${i}`} />
          <button className="admin-mini danger" onClick={() => { if (window.confirm("Delete this announcement?")) onChange({ ...value, items: items.filter((_, idx) => idx !== i) }); }} data-testid={`announcement-delete-${i}`}>Delete</button>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onChange({ ...value, items: [...items, { id: uid(), text: "" }] })} data-testid="announcement-add">+ Add Announcement</button>
    </section>
  );
}

function HoursEditor({ value, onChange }) {
  const rows = value.rows || [];
  const setRow = (i, k, v) => onChange({ ...value, rows: rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) });
  return (
    <section className="admin-section">
      <h2>Business Hours</h2>
      <label>Effective date note<input value={value.effectiveDate || ""} onChange={(e) => onChange({ ...value, effectiveDate: e.target.value })} data-testid="hours-effective" /></label>
      <label>Footnote<input value={value.notice || ""} onChange={(e) => onChange({ ...value, notice: e.target.value })} data-testid="hours-notice" /></label>
      <div className="admin-table-editor">
        {rows.map((r, i) => (
          <div className="admin-hours-row" key={i}>
            <input value={r.day || ""} onChange={(e) => setRow(i, "day", e.target.value)} placeholder="Day" data-testid={`hours-day-${i}`} />
            <input value={r.breakfast || ""} onChange={(e) => setRow(i, "breakfast", e.target.value)} placeholder="Breakfast" />
            <input value={r.kitchen || ""} onChange={(e) => setRow(i, "kitchen", e.target.value)} placeholder="Kitchen" data-testid={`hours-kitchen-${i}`} />
            <input value={r.sandTrap || ""} onChange={(e) => setRow(i, "sandTrap", e.target.value)} placeholder="Sand Trap" />
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactEditor({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <section className="admin-section">
      <h2>Contact Information</h2>
      <label>Phone (display)<input value={value.phoneDisplay || ""} onChange={set("phoneDisplay")} data-testid="contact-phone" /></label>
      <label>Email<input value={value.email || ""} onChange={set("email")} data-testid="contact-email" /></label>
      <label>Address line<input value={value.addressLine1 || ""} onChange={set("addressLine1")} data-testid="contact-address" /></label>
      <label>City, State ZIP<input value={value.cityStateZip || ""} onChange={set("cityStateZip")} data-testid="contact-city" /></label>
      <label>Google rating text<input value={value.googleRating || ""} onChange={set("googleRating")} data-testid="contact-rating" /></label>
    </section>
  );
}

function PriceRows({ title, rows, onRows, prefix }) {
  const set = (i, k, v) => onRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="admin-subblock">
      <h3>{title}</h3>
      {rows.map((r, i) => (
        <div className="admin-row" key={i}>
          <input value={r.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="Label" data-testid={`${prefix}-label-${i}`} />
          <input value={r.price} onChange={(e) => set(i, "price", e.target.value)} placeholder="Price" data-testid={`${prefix}-price-${i}`} className="admin-price" />
          <button className="admin-mini danger" onClick={() => { if (window.confirm("Delete this rate row?")) onRows(rows.filter((_, idx) => idx !== i)); }}>Delete</button>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onRows([...rows, { label: "", price: "" }])} data-testid={`${prefix}-add`}>+ Add Row</button>
    </div>
  );
}

function RatesEditor({ value, onChange }) {
  return (
    <section className="admin-section">
      <h2>Rates &amp; Specials</h2>
      <label>Specials (shown on Rates page)
        <textarea rows={3} value={value.specials || ""} onChange={(e) => onChange({ ...value, specials: e.target.value })} placeholder="e.g. Twilight special: $30 after 4pm on weekdays" data-testid="rates-specials" />
      </label>
      <PriceRows title="Weekday Rates" rows={value.weekday || []} onRows={(r) => onChange({ ...value, weekday: r })} prefix="weekday" />
      <PriceRows title="Weekend Rates" rows={value.weekend || []} onRows={(r) => onChange({ ...value, weekend: r })} prefix="weekend" />
    </section>
  );
}

function BanquetInfoEditor({ value, onChange }) {
  return (
    <section className="admin-section">
      <h2>Banquet Information</h2>
      <label>Heading<input value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="banquet-title" /></label>
      <label>Description<textarea rows={4} value={value.description || ""} onChange={(e) => onChange({ ...value, description: e.target.value })} data-testid="banquet-desc" /></label>
    </section>
  );
}

function GalleryList({ title, listKey, value, onChange, onStatus, captionFields = ["alt"] }) {
  const list = value[listKey] || [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const setItems = (items) => onChange({ ...value, [listKey]: items });
  const setCaption = (i, text) =>
    setItems(list.map((it, idx) => (idx === i ? { ...it, ...Object.fromEntries(captionFields.map((f) => [f, text])) } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
  };
  const remove = (i) => { if (window.confirm("Delete this photo from the gallery?")) setItems(list.filter((_, idx) => idx !== i)); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const data = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      setItems([...list, { id: uid(), src: data.url, alt: "" }]);
      onStatus("success", "Photo uploaded. Remember to Save Draft then Publish.");
    } catch (err) {
      onStatus("error", err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="admin-subblock">
      <h3>{title}</h3>
      <div className="admin-gallery-grid">
        {list.map((it, i) => (
          <div className="admin-gallery-item" key={it.id || i} data-testid={`gallery-${listKey}-item-${i}`}>
            <div className="admin-gallery-thumb">
              <img src={it.src && it.src.startsWith("/api/") ? `${API}${it.src}` : it.src} alt={it.alt || "gallery"} />
            </div>
            <input value={it.alt || ""} onChange={(e) => setCaption(i, e.target.value)} placeholder="Caption / description" data-testid={`gallery-${listKey}-caption-${i}`} />
            <div className="admin-gallery-controls">
              <button className="admin-mini" onClick={() => move(i, -1)} data-testid={`gallery-${listKey}-up-${i}`}>↑</button>
              <button className="admin-mini" onClick={() => move(i, 1)} data-testid={`gallery-${listKey}-down-${i}`}>↓</button>
              <button className="admin-mini danger" onClick={() => remove(i)} data-testid={`gallery-${listKey}-delete-${i}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid={`gallery-${listKey}-file`} />
      <button className="admin-mini" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`gallery-${listKey}-upload`}>
        {uploading ? "Uploading…" : "+ Upload Photo"}
      </button>
    </div>
  );
}

function GalleryEditor({ value, onChange, onStatus }) {
  return (
    <section className="admin-section">
      <h2>Banquet &amp; Gallery Photos</h2>
      <p className="admin-hint">Upload, caption, reorder, or delete photos. Save Draft, then Publish to make changes live.</p>
      <GalleryList title="Main Gallery (landscape)" listKey="gallery" value={value} onChange={onChange} onStatus={onStatus} />
      <GalleryList title="Portrait Gallery (vertical)" listKey="portraitGallery" value={value} onChange={onChange} onStatus={onStatus} />
    </section>
  );
}

function HeroImageField({ value, onChange, onStatus, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const img = value || {};
  const src = img.src && img.src.startsWith("/api/") ? `${API}${img.src}` : img.src;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const d = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      onChange({ ...img, src: d.url });
      onStatus("success", "Hero image uploaded. Save Draft, then Publish.");
    } catch (err) {
      onStatus("error", err.message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="admin-subblock">
      <h3>Hero Image</h3>
      <div className="admin-hero-preview">{src ? <img src={src} alt={img.alt || "hero"} /> : null}</div>
      <input value={img.alt || ""} onChange={(e) => onChange({ ...img, alt: e.target.value })} placeholder="Image description (alt text)" data-testid={`${testid}-alt`} />
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid={`${testid}-file`} />
      <button className="admin-mini" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-upload`}>
        {busy ? "Uploading…" : "Replace Hero Image"}
      </button>
    </div>
  );
}

function StringList({ title, items, onChange, prefix }) {
  return (
    <div className="admin-subblock">
      <h3>{title}</h3>
      {items.map((it, i) => (
        <div className="admin-row" key={i}>
          <input value={it} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} data-testid={`${prefix}-${i}`} />
          <button className="admin-mini danger" onClick={() => { if (window.confirm("Delete this item?")) onChange(items.filter((_, idx) => idx !== i)); }}>Delete</button>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onChange([...items, ""])} data-testid={`${prefix}-add`}>+ Add</button>
    </div>
  );
}

function AboutEditor({ value, onChange, onStatus }) {
  return (
    <section className="admin-section">
      <h2>About Page</h2>
      <label>Title<input value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="about-title" /></label>
      <label>Body (blank line between paragraphs)<textarea rows={6} value={value.body || ""} onChange={(e) => onChange({ ...value, body: e.target.value })} data-testid="about-body" /></label>
      <StringList title="Club Features" items={value.features || []} onChange={(f) => onChange({ ...value, features: f })} prefix="about-feature" />
      <HeroImageField value={value.heroImage} onChange={(h) => onChange({ ...value, heroImage: h })} onStatus={onStatus} testid="about-hero" />
    </section>
  );
}

function MembershipsEditor({ value, onChange, onStatus }) {
  const introText = Array.isArray(value.intro) ? value.intro.join("\n\n") : value.intro || "";
  const setPricing = (i, k, v) => onChange({ ...value, pricing: value.pricing.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) });
  return (
    <section className="admin-section">
      <h2>Memberships Page</h2>
      <label>Intro (blank line between paragraphs)<textarea rows={5} value={introText} onChange={(e) => onChange({ ...value, intro: e.target.value.split(/\n\n+/) })} data-testid="mem-intro" /></label>
      <StringList title="Benefits" items={value.benefits || []} onChange={(b) => onChange({ ...value, benefits: b })} prefix="mem-benefit" />
      <div className="admin-subblock">
        <h3>Pricing</h3>
        {(value.pricing || []).map((p, i) => (
          <div className="admin-card-editor" key={i}>
            <input value={p.name || ""} onChange={(e) => setPricing(i, "name", e.target.value)} placeholder="Plan name" data-testid={`mem-price-name-${i}`} />
            <input value={p.description || ""} onChange={(e) => setPricing(i, "description", e.target.value)} placeholder="Description (optional)" />
            <div className="admin-row">
              <input value={p.monthly || ""} onChange={(e) => setPricing(i, "monthly", e.target.value)} placeholder="Monthly" data-testid={`mem-price-monthly-${i}`} />
              <input value={p.yearly || ""} onChange={(e) => setPricing(i, "yearly", e.target.value)} placeholder="Yearly" />
            </div>
            <button className="admin-mini danger" onClick={() => { if (window.confirm("Delete this plan?")) onChange({ ...value, pricing: value.pricing.filter((_, idx) => idx !== i) }); }}>Delete Plan</button>
          </div>
        ))}
        <button className="admin-mini" onClick={() => onChange({ ...value, pricing: [...(value.pricing || []), { name: "", description: "", monthly: "", yearly: "" }] })} data-testid="mem-price-add">+ Add Plan</button>
      </div>
      <label>Initiation Fee text<textarea rows={3} value={value.initiationFee || ""} onChange={(e) => onChange({ ...value, initiationFee: e.target.value })} data-testid="mem-initiation" /></label>
      <HeroImageField value={value.heroImage} onChange={(h) => onChange({ ...value, heroImage: h })} onStatus={onStatus} testid="mem-hero" />
    </section>
  );
}

function JuniorGolfEditor({ value, onChange, onStatus }) {
  return (
    <section className="admin-section">
      <h2>Junior Golf Page</h2>
      <label>Title<input value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="jg-title" /></label>
      <label>Status message<input value={value.status || ""} onChange={(e) => onChange({ ...value, status: e.target.value })} data-testid="jg-status" /></label>
      <HeroImageField value={value.heroImage} onChange={(h) => onChange({ ...value, heroImage: h })} onStatus={onStatus} testid="jg-hero" />
      <GalleryList title="Junior Golf Gallery" listKey="gallery" value={value} onChange={onChange} onStatus={onStatus} />
    </section>
  );
}

function JuniorCampEditor({ value, onChange, onStatus }) {
  const setDetail = (i, k, v) => onChange({ ...value, details: value.details.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) });
  return (
    <section className="admin-section">
      <h2>Junior Camp Page</h2>
      <label>Title<input value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="jc-title" /></label>
      <label>Mission<textarea rows={4} value={value.mission || ""} onChange={(e) => onChange({ ...value, mission: e.target.value })} data-testid="jc-mission" /></label>
      <div className="admin-subblock">
        <h3>Program Details</h3>
        {(value.details || []).map((d, i) => (
          <div className="admin-row" key={i}>
            <input value={d.label || ""} onChange={(e) => setDetail(i, "label", e.target.value)} placeholder="Label" data-testid={`jc-detail-label-${i}`} />
            <input value={d.value || ""} onChange={(e) => setDetail(i, "value", e.target.value)} placeholder="Value" data-testid={`jc-detail-value-${i}`} />
            <button className="admin-mini danger" onClick={() => { if (window.confirm("Delete this detail?")) onChange({ ...value, details: value.details.filter((_, idx) => idx !== i) }); }}>Delete</button>
          </div>
        ))}
        <button className="admin-mini" onClick={() => onChange({ ...value, details: [...(value.details || []), { label: "", value: "" }] })} data-testid="jc-detail-add">+ Add Detail</button>
      </div>
      <StringList title="Camp Components" items={value.components || []} onChange={(c) => onChange({ ...value, components: c })} prefix="jc-component" />
      <StringList title="Core Values" items={value.coreValues || []} onChange={(c) => onChange({ ...value, coreValues: c })} prefix="jc-value" />
      <HeroImageField value={value.heroImage} onChange={(h) => onChange({ ...value, heroImage: h })} onStatus={onStatus} testid="jc-hero" />
    </section>
  );
}

function SandTrapEditor({ value, onChange, onStatus }) {
  return (
    <section className="admin-section">
      <h2>Sand Trap Page</h2>
      <label>Subtitle<input value={value.subtitle || ""} onChange={(e) => onChange({ ...value, subtitle: e.target.value })} data-testid="st-subtitle" /></label>
      <label>Title<input value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="st-title" /></label>
      <label>Intro (blank line between paragraphs)<textarea rows={5} value={value.intro || ""} onChange={(e) => onChange({ ...value, intro: e.target.value })} data-testid="st-intro" /></label>
      <HeroImageField value={value.heroImage} onChange={(h) => onChange({ ...value, heroImage: h })} onStatus={onStatus} testid="st-hero" />
      <GalleryList title="Menu Images" listKey="menuGallery" value={value} onChange={onChange} onStatus={onStatus} captionFields={["label", "alt"]} />
    </section>
  );
}
